import axios from "@/utils/axios";
import { useToast } from "@elaniintc/goes-design-system";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ValidationError } from "./error-handling";

export const useDuplicateNode = (flowId: string) => {
  const query = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (data: { node_id: string; flow_id: string }) => {
      const response = await axios.post(
        `/admin/v1/node/duplicate/${data.node_id}/${data.flow_id}`,
      );
      return response.data;
    },
    onSuccess() {
      // Invalidate the flow queries to refresh the canvas with the newly cloned node
      query.invalidateQueries({
        queryKey: ["admin-flow-detail", flowId],
      });
      // Also invalidate any flow-related query to keep consistency
      query.invalidateQueries({
        queryKey: ["admin-flow-detail"],
      });
      toast.showToast({
        title: "Nodo clonado",
        description: "El nodo ha sido clonado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    },
    onError() {
      toast.showToast({
        title: "Error al clonar nodo",
        description: "Ocurrió un error al intentar clonar el nodo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

// Clone form node
interface CloneNodeBody {
  target_flow_id?: string;
  name?: string;
}

export const useCloneNode = (flowId: string) => {
  const query = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (params: { nodeId: string; body?: CloneNodeBody }) => {
      const { nodeId, body } = params;
      const res = await axios.post(
        `/admin/v1/flows/${flowId}/nodes/${nodeId}/duplicate`,
        body ?? {},
      );
      return res.data;
    },
    onSuccess() {
      query.invalidateQueries({ queryKey: ["admin-flow-detail", flowId] });
      query.invalidateQueries({ queryKey: ["admin-flow-detail"] });

      toast.showToast({
        title: "Nodo clonado",
        description: "El nodo ha sido clonado correctamente.",
        options: { type: "success", duration: 5000 },
      });
    },
    onError(error: ValidationError) {
      let title = "Error al clonar nodo";
      let description =
        error?.response?.data?.message ??
        "Ocurrió un error al intentar clonar el nodo.";

      if (error?.response?.status === 422) {
        const errs = error.response.data?.errors;
        if (errs?.name?.includes("validation.max.string")) {
          title = "Nombre demasiado largo";
          description = "El nombre no debe superar 255 caracteres.";
        } else if (errs?.name?.includes("Ya existe un nodo con este nombre")) {
          title = "Nombre existente";
          description = "Ya existe un nodo con este nombre.";
        } else {
          title = "Ha ocurrido un error";
          description =
            "Los datos proporcionados no son válidos. Verifica la información e intenta nuevamente.";
        }
      } else if (error?.response?.status === 404) {
        title = "No encontrado";
        description = "No se encontró el flujo o el nodo que intentas clonar.";
      } else if (error?.response?.status === 403) {
        title = "Sin permisos";
        description = "No tienes permisos para clonar nodos en este flujo.";
      }

      toast.showToast({
        title,
        description,
        options: { type: "error", duration: 5000 },
      });
    },
  });
};

export const useRenameConnectionLabel = (flowId: string) => {
  const query = useQueryClient();
  return useMutation({
    mutationFn: async ({
      connectionId,
      label,
    }: {
      connectionId: string;
      label: string;
    }) => {
      const response = await axios.patch(
        `/admin/v1/node/connection/${connectionId}/label`,
        {
          display_name: label,
        },
      );
      return response.data;
    },
    onSuccess() {
      query.invalidateQueries({ queryKey: ["admin-flow-detail", flowId] });
      query.invalidateQueries({ queryKey: ["admin-flow-detail"] });
    },
  });
};
